import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCheckCircle, FaEye, FaEyeSlash, FaLock, FaShieldAlt, FaTimes } from 'react-icons/fa';
import AdminAuthService from '../services/AdminAuthService';
import { useTheme } from '../themeConfig';

const PasswordField = ({
  label,
  value,
  onChange,
  showPassword,
  onTogglePassword,
  placeholder,
  autoFocus = false,
}) => {
  const { theme } = useTheme();

  return (
    <label className="block">
      <span className="lumirae-ds-field-label">{label}</span>
      <div className="relative mt-2">
        <input
          autoFocus={autoFocus}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="lumirae-ds-input px-5 py-4 pr-14 text-sm font-black"
          placeholder={placeholder}
          required
        />
        <button
          type="button"
          onClick={onTogglePassword}
          className="absolute right-4 top-1/2 -translate-y-1/2"
          style={{ color: theme.accent }}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
        </button>
      </div>
    </label>
  );
};

const PasswordRule = ({ passed, children }) => {
  const { theme } = useTheme();
  return (
    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest" style={{color: passed ? theme.stateSuccess : theme.textMuted}}>
      <FaCheckCircle className={passed ? '' : 'opacity-35'} />
      <span>{children}</span>
    </div>
  );
};

const ForcePasswordChangeModal = ({ isOpen, pageId, oldPassword, onSuccess, onCancel }) => {
  const { theme } = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isForcedChange = Boolean(oldPassword);
  const passwordChecks = useMemo(() => ({
    length: newPassword.length >= 8,
    letters: /[A-Za-z]/.test(newPassword),
    numbers: /[0-9]/.test(newPassword),
    different: newPassword !== '123456' && newPassword.length > 0,
    match: newPassword.length > 0 && newPassword === confirmPassword,
  }), [confirmPassword, newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const passwordToVerify = oldPassword || currentPassword;

    if (!passwordToVerify) {
      setError('Current password is required.');
      return;
    }

    if (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('Password must be at least 8 characters and include both letters and numbers.');
      return;
    }

    if (newPassword === '123456') {
      setError('Cannot use 123456 as a new password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const result = await AdminAuthService.changeAdminPassword(pageId, passwordToVerify, newPassword);
      if (result.success) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onSuccess(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/35 p-5 backdrop-blur-xl">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="lumirae-ds-panel relative z-10 w-full max-w-sm p-6 shadow-2xl"
            style={{
              '--lumirae-accent-primary': theme.accent,
              '--lumirae-accent-soft': theme.accentSoft,
              '--lumirae-surface-raised': theme.cardBg,
              '--lumirae-border-default': theme.cardBorder,
              '--lumirae-text-primary': theme.text,
              '--lumirae-text-secondary': theme.secondaryText,
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              className="lumirae-ds-icon-button absolute right-5 top-5"
              aria-label="Cancel password change"
            >
              <FaTimes size={18} />
            </button>

            <div className="flex flex-col items-center pt-4 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-[1.35rem] text-white shadow-xl" style={{ backgroundColor: theme.accent }}>
                <FaShieldAlt size={30} />
              </div>

              <div className="mt-5 space-y-2">
                <p className="lumirae-ds-meta" style={{ color: theme.accent }}>Secure Gift Studio</p>
                <h2 className="text-2xl font-black uppercase leading-tight" style={{ color: theme.text }}>
                  {isForcedChange ? 'Security update' : 'Change password'}
                </h2>
                <p className="mx-auto max-w-xs text-xs font-bold leading-relaxed" style={{ color: theme.secondaryText }}>
                  {isForcedChange
                    ? 'Change the default password before editing this personal digital gift.'
                    : 'Verify the current password, then choose a stronger one for this gift page.'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mt-6 w-full space-y-4 text-left">
                {!isForcedChange && (
                  <PasswordField
                    autoFocus
                    label="Current Password"
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword((value) => !value)}
                    placeholder="Enter current password"
                  />
                )}

                <PasswordField
                  autoFocus={isForcedChange}
                  label="New Password"
                  value={newPassword}
                  onChange={setNewPassword}
                  showPassword={showPassword}
                  onTogglePassword={() => setShowPassword((value) => !value)}
                  placeholder="At least 8 chars, letters & numbers"
                />

                <PasswordField
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  showPassword={showPassword}
                  onTogglePassword={() => setShowPassword((value) => !value)}
                  placeholder="Confirm your new password"
                />

                <div className="lumirae-ds-soft-note space-y-2 p-4">
                  <PasswordRule passed={passwordChecks.length}>8+ characters</PasswordRule>
                  <PasswordRule passed={passwordChecks.letters}>Includes letters</PasswordRule>
                  <PasswordRule passed={passwordChecks.numbers}>Includes numbers</PasswordRule>
                  <PasswordRule passed={passwordChecks.different}>Not the default password</PasswordRule>
                  <PasswordRule passed={passwordChecks.match}>Passwords match</PasswordRule>
                </div>

                {error && (
                  <p className="lumirae-ds-soft-note p-3 text-xs font-black uppercase leading-relaxed tracking-[0.12em]" style={{ color: theme.stateError }}>
                    {error}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-4"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="lumirae-ds-button lumirae-ds-button-primary flex items-center justify-center gap-2 px-5 py-4 disabled:opacity-55"
                  >
                    {loading ? 'Updating...' : <><FaLock size={13} /> Update</>}
                  </button>
                </div>
              </form>

              <p className="lumirae-ds-meta mt-5">Use a password you do not use elsewhere.</p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ForcePasswordChangeModal;
